import type { ReactNode } from "react";

import { Typography } from "@heroui/react";
import clsx from "clsx";

import { AuthHeroPanel } from "./hero-panel";

import { CONSTANTS } from "@/config/constants";
import { useLogo } from "@/hooks/use-logo";

type AuthSplitShellProps = {
  title: string;
  description: string;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * Width of the form column. Onboarding runs six fields deep and reads as a
   * cramped ribbon at the sign-in width, so it opts up rather than every page
   * widening to suit the longest one.
   */
  contentClassName?: string;
  /** Overrides the hero's marketing headline. See ONBOARDING_HERO_HEADLINE. */
  heroHeadline?: string;
};

// Two-column shell for the unauthenticated pages. Below `lg` the hero drops
// out and the form centres on its own.
export const AuthSplitShell = ({
  title,
  description,
  children,
  footer,
  contentClassName = "max-w-sm",
  heroHeadline,
}: AuthSplitShellProps) => {
  const { full: logoSrc } = useLogo();

  return (
    <div className="grid min-h-dvh grid-cols-1 lg:grid-cols-2">
      <AuthHeroPanel headline={heroHeadline} />

      <div className="bg-background flex items-center justify-center px-6 py-12">
        <div className={clsx("flex w-full flex-col gap-6", contentClassName)}>
          {/* Centred rather than flush with the form: with the hero gone this
          is the only brand on the page. */}
          <img
            alt={CONSTANTS.BRAND_NAME}
            className="h-8 self-center object-contain lg:hidden"
            src={logoSrc}
          />

          <div className="flex flex-col gap-1.5">
            <Typography type="h3">{title}</Typography>
            <Typography color="muted" type="body-sm">
              {description}
            </Typography>
          </div>

          {children}

          {footer}
        </div>
      </div>
    </div>
  );
};
